import React from 'react';
import { graphql, useQueryLoader } from 'react-relay';

import { useSubscribe } from '@atlaskit/rovo-triggers/main';
import { normalizeAccountIdFromIdentityAri } from '@atlassian/conversation-assistant-utils/default-agent';

import { expValEquals } from '@confluence/feature-experiments';

import type { PrefetchTelepointerAvatarsQuery } from './__generated__/PrefetchTelepointerAvatarsQuery.graphql.ts';

const AI_MATE_SUBSCRIPTION_OPTIONS = { topic: 'ai-mate' } as const;

const EnabledPrefetchTelepointerAvatars = (): null => {
	// oxlint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook -- Relay's query loader is not a sweet-state hook; it retains the prefetched data until disposal.
	const [, loadAvatar, disposeAvatar] = useQueryLoader<PrefetchTelepointerAvatarsQuery>(graphql`
		# Warm the shared Relay cache for TelepointerAgentAvatar in @atlassian/editor-plugin-ai.
		# eslint-disable-next-line @atlassian/relay/unused-fields
		query PrefetchTelepointerAvatarsQuery($accountId: ID!, $shouldFetchAvatar: Boolean!) {
			user(accountId: $accountId) @include(if: $shouldFetchAvatar) {
				name
				picture
				...avatarRelay_user
			}
		}
	`);

	useSubscribe(AI_MATE_SUBSCRIPTION_OPTIONS, (payload) => {
		if (payload.type !== 'agent-changed') {
			return;
		}
		const accountId = normalizeAccountIdFromIdentityAri(payload.data.agent?.identityAccountId);
		if (!accountId) {
			disposeAvatar();
			return;
		}
		loadAvatar({ accountId, shouldFetchAvatar: true }, { fetchPolicy: 'store-or-network' });
	});

	return null;
};

/** Warm the same Confluence Relay store that the editor's TelepointerAgentAvatar reads. */
export const PrefetchTelepointerAvatars = (): React.JSX.Element | null => {
	if (!expValEquals('platform_editor_agent_avatar_prefetch', 'isEnabled', true)) {
		return null;
	}
	return <EnabledPrefetchTelepointerAvatars />;
};
